import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Textarea } from '@/components/ui/textarea.js';
import { Switch } from '@/components/ui/switch.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.js';
import { X, Plus, Loader2, Clock, Calendar } from 'lucide-react';
import { useServices } from '@/contexts/ServicesContext.js';
import { useSchedulerStore } from '@/stores/schedulerStore.js';

interface CreateScheduleModalProps {
  onClose: () => void;
}

type FrequencyType = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';

interface ScheduleConfig {
  frequency: FrequencyType;
  // Hourly
  hourlyMinute?: number;
  // Daily
  dailyTime?: string; // HH:mm format
  // Weekly
  weeklyDays?: number[]; // 0=Sunday, 1=Monday, etc.
  weeklyTime?: string;
  // Monthly
  monthlyDay?: number; // 1-31
  monthlyTime?: string;
  // Custom
  customCron?: string;
}

const DAYS_OF_WEEK = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

export function CreateScheduleModal({ onClose }: CreateScheduleModalProps) {
  const { apiClient } = useServices();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    instruction: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    enabled: true,
  });

  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>({
    frequency: 'daily',
    dailyTime: '09:00',
  });

  // Generate cron expression from user-friendly config
  const cronExpression = useMemo(() => {
    const { frequency } = scheduleConfig;

    switch (frequency) {
      case 'hourly': {
        const minute = scheduleConfig.hourlyMinute ?? 0;
        return `${minute} * * * *`;
      }
      case 'daily': {
        const [hour, minute] = (scheduleConfig.dailyTime || '09:00').split(':').map(Number);
        return `${minute} ${hour} * * *`;
      }
      case 'weekly': {
        const days = scheduleConfig.weeklyDays || [1]; // Default Monday
        const [hour, minute] = (scheduleConfig.weeklyTime || '09:00').split(':').map(Number);
        return `${minute} ${hour} * * ${days.join(',')}`;
      }
      case 'monthly': {
        const day = scheduleConfig.monthlyDay || 1;
        const [hour, minute] = (scheduleConfig.monthlyTime || '09:00').split(':').map(Number);
        return `${minute} ${hour} ${day} * *`;
      }
      case 'custom': {
        return scheduleConfig.customCron || '0 9 * * *';
      }
      default:
        return '0 9 * * *';
    }
  }, [scheduleConfig]);

  // Human-readable description
  const cronDescription = useMemo(() => {
    const { frequency } = scheduleConfig;

    switch (frequency) {
      case 'hourly': {
        const minute = scheduleConfig.hourlyMinute ?? 0;
        return `Every hour at ${minute} minutes past`;
      }
      case 'daily': {
        const time = scheduleConfig.dailyTime || '09:00';
        return `Every day at ${time}`;
      }
      case 'weekly': {
        const days = scheduleConfig.weeklyDays || [1];
        const dayNames = days.map(d => DAYS_OF_WEEK.find(dow => dow.value === d)?.label).join(', ');
        const time = scheduleConfig.weeklyTime || '09:00';
        return `Every ${dayNames} at ${time}`;
      }
      case 'monthly': {
        const day = scheduleConfig.monthlyDay || 1;
        const time = scheduleConfig.monthlyTime || '09:00';
        const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
        return `On the ${day}${suffix} of every month at ${time}`;
      }
      case 'custom': {
        return scheduleConfig.customCron || 'Custom cron expression';
      }
      default:
        return '';
    }
  }, [scheduleConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      console.log('[CreateScheduleModal] Creating schedule:', formData, cronExpression);

      const schedule = await apiClient.createSchedule({
        name: formData.name,
        cronExpression: cronExpression,
        instruction: formData.instruction,
        timezone: formData.timezone,
        enabled: formData.enabled,
      });

      console.log('[CreateScheduleModal] ✅ Schedule created successfully', schedule);

      // Add the created schedule to the store
      useSchedulerStore.getState().addSchedule(schedule);

      // Also refresh the full list
      try {
        const schedules = await apiClient.listSchedules();
        console.log('[CreateScheduleModal] 📋 Refreshed schedules list:', schedules);
        useSchedulerStore.getState().setSchedules(schedules);
      } catch (err) {
        console.warn('[CreateScheduleModal] Failed to refresh schedule list:', err);
      }

      onClose();
    } catch (err) {
      console.error('[CreateScheduleModal] ❌ Error creating schedule:', err);
      setError(err instanceof Error ? err.message : 'Failed to create schedule');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleWeekday = (day: number) => {
    const currentDays = scheduleConfig.weeklyDays || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort();
    setScheduleConfig({ ...scheduleConfig, weeklyDays: newDays.length > 0 ? newDays : [day] });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-background border rounded-lg shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Create New Routine</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Routine Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Daily standup reminder"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          {/* Frequency Selector */}
          <div className="space-y-3 p-3 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <Label>When should this run? *</Label>
            </div>

            <Select
              value={scheduleConfig.frequency}
              onValueChange={(value: FrequencyType) =>
                setScheduleConfig({
                  frequency: value,
                  hourlyMinute: value === 'hourly' ? 0 : undefined,
                  dailyTime: value === 'daily' ? '09:00' : undefined,
                  weeklyDays: value === 'weekly' ? [1] : undefined,
                  weeklyTime: value === 'weekly' ? '09:00' : undefined,
                  monthlyDay: value === 'monthly' ? 1 : undefined,
                  monthlyTime: value === 'monthly' ? '09:00' : undefined,
                  customCron: value === 'custom' ? '' : undefined,
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom (Cron)</SelectItem>
              </SelectContent>
            </Select>

            {/* Hourly Options */}
            {scheduleConfig.frequency === 'hourly' && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="hourlyMinute">At minute</Label>
                <Input
                  id="hourlyMinute"
                  type="number"
                  min="0"
                  max="59"
                  value={scheduleConfig.hourlyMinute ?? 0}
                  onChange={(e) =>
                    setScheduleConfig({
                      ...scheduleConfig,
                      hourlyMinute: parseInt(e.target.value) || 0,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">0-59 minutes past the hour</p>
              </div>
            )}

            {/* Daily Options */}
            {scheduleConfig.frequency === 'daily' && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="dailyTime">At time</Label>
                <Input
                  id="dailyTime"
                  type="time"
                  value={scheduleConfig.dailyTime || '09:00'}
                  onChange={(e) =>
                    setScheduleConfig({ ...scheduleConfig, dailyTime: e.target.value })
                  }
                />
              </div>
            )}

            {/* Weekly Options */}
            {scheduleConfig.frequency === 'weekly' && (
              <div className="space-y-3 pt-2">
                <div className="space-y-2">
                  <Label>On days</Label>
                  <div className="flex gap-1">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleWeekday(day.value)}
                        className={`flex-1 px-2 py-1.5 text-xs font-medium rounded border transition-colors ${
                          (scheduleConfig.weeklyDays || []).includes(day.value)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background border-border hover:bg-muted'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weeklyTime">At time</Label>
                  <Input
                    id="weeklyTime"
                    type="time"
                    value={scheduleConfig.weeklyTime || '09:00'}
                    onChange={(e) =>
                      setScheduleConfig({ ...scheduleConfig, weeklyTime: e.target.value })
                    }
                  />
                </div>
              </div>
            )}

            {/* Monthly Options */}
            {scheduleConfig.frequency === 'monthly' && (
              <div className="space-y-3 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="monthlyDay">On day of month</Label>
                  <Input
                    id="monthlyDay"
                    type="number"
                    min="1"
                    max="31"
                    value={scheduleConfig.monthlyDay || 1}
                    onChange={(e) =>
                      setScheduleConfig({
                        ...scheduleConfig,
                        monthlyDay: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">1-31</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="monthlyTime">At time</Label>
                  <Input
                    id="monthlyTime"
                    type="time"
                    value={scheduleConfig.monthlyTime || '09:00'}
                    onChange={(e) =>
                      setScheduleConfig({ ...scheduleConfig, monthlyTime: e.target.value })
                    }
                  />
                </div>
              </div>
            )}

            {/* Custom Cron */}
            {scheduleConfig.frequency === 'custom' && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="customCron">Cron Expression</Label>
                <Input
                  id="customCron"
                  placeholder="e.g., 0 9 * * 1-5"
                  value={scheduleConfig.customCron || ''}
                  onChange={(e) =>
                    setScheduleConfig({ ...scheduleConfig, customCron: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Format: minute hour day month weekday
                </p>
              </div>
            )}

            {/* Preview */}
            <div className="flex items-start gap-2 p-2 rounded bg-background/50 border">
              <Clock className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="text-xs space-y-1 flex-1 min-w-0">
                <p className="font-medium text-foreground">{cronDescription}</p>
                <p className="text-muted-foreground font-mono break-all">{cronExpression}</p>
              </div>
            </div>
          </div>

          {/* Instruction */}
          <div className="space-y-2">
            <Label htmlFor="instruction">What should the agent do? *</Label>
            <Textarea
              id="instruction"
              placeholder="e.g., Send a reminder message about the daily standup meeting in the team channel"
              value={formData.instruction}
              onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
              rows={3}
              required
            />
            <p className="text-xs text-muted-foreground">
              Detailed instruction for the agent to execute
            </p>
          </div>

          {/* Enabled */}
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div className="space-y-0.5">
              <Label htmlFor="enabled">Enable Routine</Label>
              <p className="text-xs text-muted-foreground">
                Routine will start running immediately if enabled
              </p>
            </div>
            <Switch
              id="enabled"
              checked={formData.enabled}
              onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Routine
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
